import type { Logger } from '@hopdrive/sdk-core'

/**
 * Generate a short unique operation ID for log correlation.
 */
export function generateOperationId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().slice(0, 8)
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
}

/**
 * Wrap a logger with operation-specific metadata.
 *
 * When the underlying logger supports `withMetadata()` (e.g., @hopdrive/sdk-server-logger's ILogger v2.0),
 * metadata becomes structured data in log JSON. Otherwise, includes labels as structured data
 * in every log call.
 */
export function scopeLogger(logger: Logger, labels: Record<string, unknown>): Logger {
  if ('withMetadata' in logger && typeof (logger as any).withMetadata === 'function') {
    return (logger as any).withMetadata(labels) as Logger
  }
  // Fallback: include labels as structured data in every call
  return {
    info: (msg, ...d) => logger.info(msg, labels, ...d),
    debug: (msg, ...d) => logger.debug(msg, labels, ...d),
    warn: (msg, ...d) => logger.warn(msg, labels, ...d),
    error: (msg, ...d) => logger.error(msg, labels, ...d),
  }
}

/**
 * Compute elapsed milliseconds since a start time.
 */
export function elapsed(startedAt: number): number {
  return Date.now() - startedAt
}
